import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormFeedback, Input, Label, Row, Col, Card, CardBody } from "reactstrap";

import * as Yup from "yup";
import { useFormik } from "formik";
import { v4 as uuidv4 } from 'uuid';

import { addNote, updateNote, addDelivery, getDelivery, updateDelivery } from "../../slices/thunks";
/*Delivery = 1, //Brza posta
Order = 2, //Narudzba
Loan = 3, //Revers
PartNote = 4, //Biljeske dijelovi
Other = 5  //Ostalo
*/
const NoteModal = ({isEdit, note, show, onCloseClick}) => {
    const noteTypes = [
        {id: 0, name: "Odaberite tip bilješke"},
        {id: 1, name: "Brza pošta"},
        {id: 2, name: "Narudžba"},
        {id: 3, name: "Revers"},
        {id: 4, name: "Bilješke dijelovi"},
        {id: 5, name: "Ostalo"}
    ];
    const dispatch = useDispatch();
    const [selectedNoteType, setSelectedNoteType] = useState(0);
    const [deliveryData, setDeliveryData] = useState(null);

    useEffect(() => {
        if (isEdit && note) {
            setSelectedNoteType(note.noteType);
            // If editing a delivery note, fetch delivery data
            if (note.noteType === 1) {
                dispatch(getDelivery(note.id)).then((response) => {
                    if (response.payload) {
                        setDeliveryData(response.payload);
                    }
                });
            }
        }
    }, [isEdit, note, show, dispatch]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: isEdit ? {
            title: note.title,
            description: note.description ?? "",
            noteType: note.noteType ?? 0
        } : { 
            title: "",
            description: "",
            noteType: 0
        },
        validationSchema: Yup.object({
            title: Yup.string().required("Molimo unesite naziv bilješke."),
            noteType: Yup.number().min(1, "Molimo odaberite tip bilješke.").required("Molimo odaberite tip bilješke.")
        }),
        onSubmit: (values) => {
            if (isEdit) {
                const updatedNote = {
                    id: note.id,
                    parentId: note.parentId,
                    isActive: note.isActive,
                    title: values.title,
                    description: values.description,
                    noteType: values.noteType                    
                }
                dispatch(updateNote(updatedNote));
            }
            else dispatch(addNote(values));
            
            handleOnCloseClick();
        },
    });

    const deliveryValidation = useFormik({
        enableReinitialize: true,
        initialValues: deliveryData || {
            nazivKlijenta: "",
            adresa: "",
            grad: "",
            telefon: "",
            opisNarudzbe: "",
            vrstaUsluge: "",
            masa: 0,
            brojPaketa: 1,
            vrstaDostave: "Euroexpress",
            iznosNaplate: 0,
            usluguPlaca: "primalac",
            otvaranje: false,
            povratZamjenske: false,
            povratAmbalaze: false,
            napomena: ""
        },
        validationSchema: Yup.object({
            nazivKlijenta: Yup.string().required("Obavezno polje"),
            adresa: Yup.string().required("Obavezno polje"),
            grad: Yup.string().required("Obavezno polje"),
            telefon: Yup.string().required("Obavezno polje"),
            vrstaUsluge: Yup.string().required("Obavezno polje"),
            vrstaDostave: Yup.string().required("Obavezno polje")
        }),
        onSubmit: (values) => {
            if (isEdit && note) {
                dispatch(updateDelivery({noteId: note.id, deliveryData: values}));
            } else {
                dispatch(addDelivery({deliveryData: values, parentId: note?.parentId}));
            }
            handleOnCloseClick();
        },
    });

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (selectedNoteType === 1 || (isEdit && note?.noteType === 1)) {
            deliveryValidation.handleSubmit();
        } else {
            validation.handleSubmit();
        }
        return false;
    };

    const handleOnCloseClick = () => {
        validation.resetForm();
        deliveryValidation.resetForm();
        setSelectedNoteType(0);
        setDeliveryData(null);
        onCloseClick();
    };

    const handleNoteTypeChange = (e) => {
        const value = parseInt(e.target.value);
        setSelectedNoteType(value);
        validation.handleChange(e);
    };

    const renderDeliveryForm = () => (
        <div className="row gy-3">
            <Col xs={12}>
                <Card className="mb-3">
                    <CardBody>
                        <h6 className="text-uppercase mb-3">Osnovni Podaci</h6>
                        <Row className="g-3">
                            <Col md={6}>
                                <Label>Naziv Klijenta *</Label>
                                <Input
                                    name="nazivKlijenta"
                                    type="text"
                                    value={deliveryValidation.values.nazivKlijenta}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.nazivKlijenta && deliveryValidation.errors.nazivKlijenta}
                                />
                                {deliveryValidation.touched.nazivKlijenta && deliveryValidation.errors.nazivKlijenta && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.nazivKlijenta}</FormFeedback>
                                )}
                            </Col>
                            <Col md={6}>
                                <Label>Telefon *</Label>
                                <Input
                                    name="telefon"
                                    type="text"
                                    value={deliveryValidation.values.telefon}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.telefon && deliveryValidation.errors.telefon}
                                />
                                {deliveryValidation.touched.telefon && deliveryValidation.errors.telefon && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.telefon}</FormFeedback>
                                )}
                            </Col>
                            <Col md={12}>
                                <Label>Adresa *</Label>
                                <Input
                                    name="adresa"
                                    type="text"
                                    value={deliveryValidation.values.adresa}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.adresa && deliveryValidation.errors.adresa}
                                />
                                {deliveryValidation.touched.adresa && deliveryValidation.errors.adresa && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.adresa}</FormFeedback>
                                )}
                            </Col>
                            <Col md={12}>
                                <Label>Grad *</Label>
                                <Input
                                    name="grad"
                                    type="text"
                                    value={deliveryValidation.values.grad}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.grad && deliveryValidation.errors.grad}
                                />
                                {deliveryValidation.touched.grad && deliveryValidation.errors.grad && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.grad}</FormFeedback>
                                )}
                            </Col>
                            <Col md={12}>
                                <Label>Opis Narudžbe</Label>
                                <Input
                                    name="opisNarudzbe"
                                    type="textarea"
                                    rows="2"
                                    value={deliveryValidation.values.opisNarudzbe}
                                    onChange={deliveryValidation.handleChange}
                                />
                            </Col>
                            <Col md={4}>
                                <Label>Usluga *</Label>
                                <Input
                                    name="vrstaUsluge"
                                    type="text"
                                    value={deliveryValidation.values.vrstaUsluge}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.vrstaUsluge && deliveryValidation.errors.vrstaUsluge}
                                />
                                {deliveryValidation.touched.vrstaUsluge && deliveryValidation.errors.vrstaUsluge && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.vrstaUsluge}</FormFeedback>
                                )}
                            </Col>
                            <Col md={4}>
                                <Label>Masa (kg)</Label>
                                <Input
                                    name="masa"
                                    type="number"
                                    step="0.1"
                                    value={deliveryValidation.values.masa}
                                    onChange={deliveryValidation.handleChange}
                                />
                            </Col>
                            <Col md={4}>
                                <Label>Broj Paketa</Label>
                                <Input
                                    name="brojPaketa"
                                    type="number"
                                    step="1"
                                    value={deliveryValidation.values.brojPaketa}
                                    onChange={deliveryValidation.handleChange}
                                />
                            </Col>
                            <Col md={12}>
                                <Label>Pošta *</Label>
                                <Input
                                    name="vrstaDostave"
                                    type="select"
                                    value={deliveryValidation.values.vrstaDostave}
                                    onChange={deliveryValidation.handleChange}
                                    onBlur={deliveryValidation.handleBlur}
                                    invalid={deliveryValidation.touched.vrstaDostave && deliveryValidation.errors.vrstaDostave}
                                >
                                    <option value="Euroexpress">Euroexpress</option>
                                    <option value="A2B">A2B</option>
                                    <option value="BH Posta - OLX">BH Posta - OLX</option>
                                </Input>
                                {deliveryValidation.touched.vrstaDostave && deliveryValidation.errors.vrstaDostave && (
                                    <FormFeedback type="invalid">{deliveryValidation.errors.vrstaDostave}</FormFeedback>
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>

            <Col xs={12}>
                <Card className="mb-3">
                    <CardBody>
                        <h6 className="text-uppercase mb-3">Finansije</h6>
                        <Row className="g-3">
                            <Col md={6}>
                                <Label>Iznos Naplate (KM)</Label>
                                <Input
                                    name="iznosNaplate"
                                    type="number"
                                    step="0.01"
                                    value={deliveryValidation.values.iznosNaplate}
                                    onChange={deliveryValidation.handleChange}
                                />
                            </Col>
                            <Col md={6}>
                                <Label>Uslugu Plaća</Label>
                                <Input
                                    name="usluguPlaca"
                                    type="select"
                                    value={deliveryValidation.values.usluguPlaca}
                                    onChange={deliveryValidation.handleChange}
                                >
                                    <option value="posiljalac">Pošiljalac</option>
                                    <option value="primalac">Primalac</option>
                                </Input>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>

            <Col xs={12}>
                <Card className="mb-3">
                    <CardBody>
                        <h6 className="text-uppercase mb-3">Dodatne Usluge</h6>
                        <Row className="g-3">
                            <Col md={4}>
                                <div className="form-check">
                                    <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="otvaranje"
                                        name="otvaranje"
                                        checked={deliveryValidation.values.otvaranje}
                                        onChange={deliveryValidation.handleChange}
                                    />
                                    <Label className="form-check-label" for="otvaranje">
                                        Otvaranje
                                    </Label>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="form-check">
                                    <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="povratZamjenske"
                                        name="povratZamjenske"
                                        checked={deliveryValidation.values.povratZamjenske}
                                        onChange={deliveryValidation.handleChange}
                                    />
                                    <Label className="form-check-label" for="povratZamjenske">
                                        Povrat Zamjenske
                                    </Label>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="form-check">
                                    <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="povratAmbalaze"
                                        name="povratAmbalaze"
                                        checked={deliveryValidation.values.povratAmbalaze}
                                        onChange={deliveryValidation.handleChange}
                                    />
                                    <Label className="form-check-label" for="povratAmbalaze">
                                        Povrat Ambalaže
                                    </Label>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>

            <Col xs={12}>
                <Card className="mb-3">
                    <CardBody>
                        <h6 className="text-uppercase mb-3">Napomena (Opcionalno)</h6>
                        <Row className="g-3">
                            <Col md={12}>
                                <Input
                                    name="napomena"
                                    type="textarea"
                                    rows="3"
                                    value={deliveryValidation.values.napomena}
                                    onChange={deliveryValidation.handleChange}
                                    placeholder="Dodatne napomene..."
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>
        </div>
    );

    const renderStandardForm = () => (
        <div className="row gy-4 mb-3">
                    <div className="col-md-6 input-group">                      
                        <Input name="title" type="text" className="form-control" readOnly={isEdit && note.parentId !== null}
                            placeholder="Naslov"                      
                            validate={{ required: { value: true }}}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.title}
                            invalid={validation.touched.title && validation.errors.title ? true : false}
                        />
                        {validation.touched.title && validation.errors.title ? (<FormFeedback type="invalid">{validation.errors.title}</FormFeedback>) : null}
                    </div>
                    <div className="col-md-6 input-group">
                        {(isEdit && note.parentId !== null) ? 
                            (<Input name="noteType" type="text" className="form-control" readOnly={isEdit && note.parentId !== null}
                                placeholder="Tip biljeske"                      
                                validate={{ required: { value: true }}}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={noteTypes.find(noteType => noteType.id == note.noteType).name}
                                invalid={validation.touched.noteType && validation.errors.noteType ? true : false} />) : 
                            (<Input name="noteType" type="select" className="form-select"
                                placeholder="Tip biljeske"                      
                                validate={{ required: { value: true }}}
                                onChange={handleNoteTypeChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.noteType}
                                invalid={validation.touched.noteType && validation.errors.noteType ? true : false}
                            >{noteTypes.map(noteType => { if (noteType.id == 4) return null; return (<option key={noteType.id} value={noteType.id}>{noteType.name}</option>)})}
                            </Input>)}
                        {validation.touched.noteType && validation.errors.noteType ? (<FormFeedback type="invalid">{validation.errors.noteType}</FormFeedback>) : null}
                    </div>
                    <div className="col-md-6 input-group">                      
                        <Input name="description" type="textarea" className="form-control"
                            placeholder="Opis"                      
                            validate={{ required: { value: false }}}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description}
                            invalid={validation.touched.description && validation.errors.description ? true : false}
                        />
                        {validation.touched.description && validation.errors.description ? (<FormFeedback type="invalid">{validation.errors.description}</FormFeedback>) : null}
                    </div>
                </div>
    );

    return (
        <Modal 
            id="noteModal" 
            isOpen={show} 
            toggle={handleOnCloseClick} 
            centered 
            backdrop={"static"}
            size={(selectedNoteType === 1 || (isEdit && note?.noteType === 1)) ? "xl" : "md"}
        >
            <ModalHeader className="bg-light p-3" toggle={handleOnCloseClick}>
                {isEdit ? "Uredi bilješku" : "Dodaj bilješku"}
            </ModalHeader>
            <Form className="tablelist-form" onSubmit={handleFormSubmit}>
                <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {(selectedNoteType === 1 || (isEdit && note?.noteType === 1)) ? renderDeliveryForm() : renderStandardForm()}
                </ModalBody>
                <ModalFooter>
                    <div className="hstack gap-2 justify-content-end">
                        <button type="button" className="btn btn-light" onClick={() => {handleOnCloseClick()}}> Zatvori </button>
                        <button type="submit" className="btn btn-success"> 
                            {isEdit ? "Ažuriraj" : "Dodaj"} 
                        </button>
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default NoteModal;